import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { resetBookData } from '../../store/slices/bookSlice';
import axios from 'axios';
import { endpoints } from '../../api/api';
import { useAlert } from '../../components/AlertBanner';
import { trackPdfDownloaded, trackLanded } from '../../utils/analytics';
import { Trash2 } from 'lucide-react';
import WelcomeModal from './WelcomeModal';
import * as pdfjsLib from 'pdfjs-dist';
import {
  BOOK_COVER_DIMENSIONS,
  PAPER_PROPERTIES,
  BINDING_SPINE_OFFSET,
} from '../../constants/bookFormFactor';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const BLEED = 0.25; // inches
const SLUG  = 5 / 25.4; // 5mm in inches

/**
 * Given book form-factor data, returns the fraction of the full-spread PDF
 * that corresponds to the front cover, so we can crop the rendered canvas precisely.
 * Falls back to right-1/3 when data is incomplete.
 */
function computeFrontCoverCrop(bookSize, bindingType, paperType, pageCount) {
  const dims    = BOOK_COVER_DIMENSIONS[bookSize];
  const paper   = PAPER_PROPERTIES[paperType];
  const offset  = BINDING_SPINE_OFFSET[bindingType];

  if (!dims || !paper || offset == null || !pageCount) {
    // Fallback: assume front cover is the rightmost third
    return { startRatio: 2 / 3, widthRatio: 1 / 3 };
  }

  const coverW      = dims.width;
  const spineWidthMm = ((pageCount / 2) * paper.gsm * paper.bulk) / 1000 + offset;
  const spineW      = spineWidthMm / 25.4;

  // Full PDF width = SLUG + BLEED + back + spine + front + BLEED + SLUG
  const totalW      = SLUG + BLEED + coverW + spineW + coverW + BLEED + SLUG;
  const frontStartX = SLUG + BLEED + coverW + spineW;

  return { startRatio: frontStartX / totalW, widthRatio: coverW / totalW };
}

const BookCoverImage = ({ coverImage, title, bookSize, bindingType, paperType, pageCount }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;

    const fetchImage = async () => {
      const filename = typeof coverImage === 'string'
        ? coverImage
        : coverImage?.filename;

      if (!filename) { setHasError(true); return; }

      setIsLoading(true);
      setHasError(false);

      try {
        const url = endpoints.downloadFileByFilename.replace(':filename', encodeURIComponent(filename));
        const response = await axios.get(url, { responseType: 'blob' });
        if (cancelled) return;

        const isPdf = filename.toLowerCase().endsWith('.pdf') || response.data.type === 'application/pdf';

        if (isPdf) {
          const arrayBuffer = await response.data.arrayBuffer();
          const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const page = await pdfDoc.getPage(1);
          const fullViewport = page.getViewport({ scale: 1 });

          const { startRatio, widthRatio } = computeFrontCoverCrop(bookSize, bindingType, paperType, pageCount);

          const srcX = Math.round(fullViewport.width * startRatio);
          const srcW = Math.round(fullViewport.width * widthRatio);
          const srcH = fullViewport.height;

          // Render the full page offscreen
          const offscreen = document.createElement('canvas');
          offscreen.width  = fullViewport.width;
          offscreen.height = fullViewport.height;
          await page.render({ canvasContext: offscreen.getContext('2d'), viewport: fullViewport, canvas: offscreen }).promise;

          // Crop to the front cover portion
          const scale = 0.5;
          const thumb = document.createElement('canvas');
          thumb.width  = Math.round(srcW * scale);
          thumb.height = Math.round(srcH * scale);
          thumb.getContext('2d').drawImage(
            offscreen,
            srcX, 0, srcW, srcH,
            0, 0, thumb.width, thumb.height,
          );
          if (!cancelled) setImageUrl(thumb.toDataURL());
        } else {
          blobUrl = URL.createObjectURL(response.data);
          if (!cancelled) setImageUrl(blobUrl);
        }
      } catch (err) {
        if (!cancelled) setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (coverImage) { fetchImage(); } else { setHasError(true); }

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [coverImage, bookSize, bindingType, paperType, pageCount]);

  if (isLoading) {
    return (
      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-orange-500" />
      </div>
    );
  }

  if (hasError || !imageUrl) {
    return (
      <div className="w-full h-full bg-gray-50 flex items-center justify-center p-2">
        <img src="/book-placeholder.png" alt="No cover" className="w-full h-full object-contain opacity-40" />
      </div>
    );
  }

  return <img src={imageUrl} alt={title} className="w-full h-full object-cover" onError={() => setHasError(true)} />;
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'draft':         return { label: 'Draft',          classes: 'bg-gray-100 text-gray-500' };
    case 'submitted':     return { label: 'Submitted',       classes: 'bg-blue-50 text-blue-600' };
    case 'under_review':  return { label: 'Under Review',    classes: 'bg-amber-50 text-amber-600' };
    case 'approved':      return { label: 'Approved',        classes: 'bg-green-50 text-green-600' };
    case 'published':     return { label: 'Published',       classes: 'bg-green-50 text-green-600' };
    case 'rejected':      return { label: 'Needs Revision',  classes: 'bg-red-50 text-red-600' };
    default:              return { label: status,            classes: 'bg-gray-100 text-gray-500' };
  }
};

const STATS = (books) => [
  { label: 'Total',       count: books.length },
  { label: 'Published',   count: books.filter(b => b.status === 'published' || b.status === 'approved').length },
  { label: 'In Draft',    count: books.filter(b => b.status === 'draft').length },
  { label: 'In Review',   count: books.filter(b => b.status === 'submitted' || b.status === 'under_review').length },
];

const DashboardScreen = () => {
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showAlert } = useAlert();
  const [allBooks, setAllBooks] = useState([]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  useEffect(() => {
    axios.get(endpoints.getBooks)
      .then(res => {
        const booksData = res.data?.books || [];
        setAllBooks(booksData);
        if (booksData.length === 0) setShowWelcome(true);
        trackLanded(booksData.map(b => b.title || ''));
      })
      .catch((error) => {
        if (error?.status === 401 || error?.response?.status === 401) return;
        setAllBooks([]);
      });
  }, []);

  const handleDownloadPDF = async (bookId, bookTitle) => {
    try {
      const url = endpoints.downloadMyBookPDF.replace(':id', bookId);
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${bookTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      trackPdfDownloaded();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Failed to download PDF. Please try again.', 'error');
    }
  };

  const handleDeleteBook = async (bookId) => {
    if (!window.confirm('Permanently delete this book? This cannot be undone.')) return;
    try {
      await axios.delete(endpoints.deleteBook.replace(':id', bookId));
      setAllBooks((prev) => prev.filter((b) => (b._id || b.id) !== bookId));
    } catch (error) {
      showAlert(error.response?.data?.message || 'Failed to delete book. Please try again.', 'error');
    }
  };

  const handleSendVerification = async () => {
    setSendingVerification(true);
    setVerificationError(null);
    try {
      await axios.post(endpoints.sendEmailVerification);
      setVerificationSent(true);
    } catch (error) {
      setVerificationError(error.response?.data?.message || 'Failed to send verification email. Please try again.');
    } finally {
      setSendingVerification(false);
    }
  };

  const showVerificationBanner = user?.email && !user?.emailVerified;
  const stats = STATS(allBooks);

  return (
    <div className="min-h-screen bg-gray-50">
      {showWelcome && (
        <WelcomeModal
          onGetStarted={() => { setShowWelcome(false); dispatch(resetBookData()); navigate('/books'); }}
          onClose={() => setShowWelcome(false)}
        />
      )}

      {/* Email Verification Banner */}
      {showVerificationBanner && false && (
        <div className="bg-orange-50 border-b border-orange-200 px-4 md:px-6 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {verificationSent ? (
              <p className="text-green-700 text-sm">Verification email sent to <span className="font-medium">{user.email}</span>. Check your inbox.</p>
            ) : (
              <p className="text-gray-700 text-sm">Please verify your email — <span className="font-medium">{user.email}</span></p>
            )}
            {verificationError && <p className="text-red-600 text-xs">{verificationError}</p>}
            {!verificationSent && (
              <button
                onClick={handleSendVerification}
                disabled={sendingVerification}
                className="bg-gray-900 text-white px-4 py-1.5 rounded-md text-sm hover:bg-gray-800 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {sendingVerification ? 'Sending...' : 'Send link'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="px-4 md:px-6 py-6 md:py-8 max-w-7xl mx-auto space-y-6">

        {/* Stats */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100">
            {stats.map(({ label, count }, i) => (
              <div key={label} className="px-6 py-5 relative">
                {i === 0 && <div className="absolute top-0 left-0 right-0 h-0.5 bg-orange-400 rounded-t-sm" />}
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">{label}</p>
                <p className={`text-4xl font-bold tabular-nums ${i === 0 ? 'text-orange-500' : 'text-gray-900'}`}>
                  {String(count).padStart(2, '0')}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Your Books */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-gray-900 text-lg font-semibold">Your Books</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Add New Book Card */}
            <button
              onClick={() => { dispatch(resetBookData()); navigate('/books'); }}
              className="flex flex-col items-center justify-center gap-3 min-h-[220px] border-2 border-dashed border-gray-200 rounded-2xl hover:border-orange-300 hover:bg-orange-50 transition-all group text-center p-6"
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 group-hover:bg-orange-100 flex items-center justify-center transition-colors">
                <svg className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 group-hover:text-orange-600 transition-colors">Start a new book</p>
                <p className="text-xs text-gray-400 mt-0.5">Begin your publishing journey</p>
              </div>
            </button>

            {/* Book Cards */}
            {allBooks.map((book) => {
              const isEditable = ['draft', 'rejected'].includes(book.status);
              const badge = getStatusBadge(book.status);

              return (
                <div
                  key={book._id || book.id}
                  className="bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow flex flex-col"
                >
                  <div className="flex items-start gap-4 mb-4 flex-1">
                    <div className="w-16 h-24 rounded-lg flex-shrink-0 overflow-hidden border border-gray-100 bg-gray-50">
                      <BookCoverImage
                        coverImage={book.coverImage}
                        title={book.title}
                        bookSize={book.bookSize}
                        bindingType={book.bindingType}
                        paperType={book.paperType}
                        pageCount={book.pageCount}
                      />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-start justify-between gap-1 mb-1">
                        <h3 className="text-gray-900 font-medium text-sm leading-snug line-clamp-2">{book.title}</h3>
                        <button
                          title="Delete book"
                          onClick={() => handleDeleteBook(book._id || book.id)}
                          className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0 mt-0.5"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      {book.author && (
                        <p className="text-gray-400 text-xs mb-2 truncate">{book.author}</p>
                      )}
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                        {badge.label}
                      </span>
                    </div>
                  </div>

                  <button
                    className="w-full py-2 rounded-lg text-sm font-medium transition-colors border border-orange-200 text-orange-600 hover:bg-orange-50"
                    onClick={() => {
                      if (isEditable) {
                        navigate(`/books?id=${book._id || book.id}`);
                      } else {
                        handleDownloadPDF(book._id || book.id, book.title);
                      }
                    }}
                  >
                    {isEditable ? 'Continue Editing' : 'Download PDF'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardScreen;
